import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/FilterBar";
import { Panel } from "@/components/KpiCard";
import { api, errMsg } from "@/lib/api";

const JOURS = ["L", "M", "M", "J", "V", "S", "D"];
const COULEUR = { Airbnb: "bg-[#FF385C] border-[#E0294B]", Booking: "bg-[#003580] border-[#00224f]" };
const moisCourant = () => new Date().toISOString().slice(0, 7);
const decaler = (m, n) => { const d = new Date(`${m}-01T12:00:00`); d.setMonth(d.getMonth() + n); return d.toISOString().slice(0, 7); };
const libelleMois = (m) => new Date(`${m}-01T12:00:00`).toLocaleDateString("fr-CA", { month: "long", year: "numeric" });
const grille = (nb) => ({ gridTemplateColumns: `170px repeat(${nb}, minmax(30px, 1fr))` });
const weekend = (cal, j) => (cal.premier_jour + j - 1) % 7 >= 5;

const EnTete = ({ cal, aujourdhui }) => (
  <div className="grid sticky top-0 z-10 bg-white border-b border-slate-200" style={grille(cal.jours)} data-testid="calendrier-entete">
    <div className="sticky left-0 z-[2] bg-white px-2 py-1.5 text-[11px] font-mono uppercase tracking-wider text-slate-500">Appartement</div>
    {Array.from({ length: cal.jours }, (_, i) => i + 1).map((j) => (
      <div key={j} className={`py-1 text-center text-[10px] tabular ${weekend(cal, j) ? "bg-slate-50" : ""} ${aujourdhui === j ? "text-emerald-700 font-bold" : "text-slate-500"}`}>
        <div>{JOURS[(cal.premier_jour + j - 1) % 7]}</div><div className="text-xs">{j}</div>
      </div>
    ))}
  </div>
);

const Barre = ({ s, mois }) => (
  <div style={{ gridColumn: `${s.jour_debut + 1} / ${s.jour_fin + 2}`, gridRow: 1 }}
    className={`z-[1] my-2 mx-0.5 flex items-center overflow-hidden border px-1.5 text-[11px] font-medium text-white shadow-sm transition-transform hover:-translate-y-0.5 ${COULEUR[s.plateforme]} ${s.a_venir ? "opacity-80 border-dashed bg-[length:8px_8px] bg-[linear-gradient(45deg,rgba(255,255,255,.18)_25%,transparent_25%,transparent_50%,rgba(255,255,255,.18)_50%,rgba(255,255,255,.18)_75%,transparent_75%)]" : ""} ${s.debut.slice(0, 7) === mois ? "rounded-l-md" : ""} ${s.fin <= `${decaler(mois, 1)}-01` ? "rounded-r-md" : ""}`}
    title={`${s.nom_client || s.code} · ${s.plateforme} · ${s.debut} → ${s.fin} · ${s.nuits} nuit(s)${s.a_venir ? " · à venir" : ""}`}
    data-testid={`calendrier-sejour-${s.code}`}>
    <span className="truncate">{s.nom_client || s.code}</span>
  </div>
);

const Rangee = ({ l, cal, mois, idx }) => (
  <div className="grid border-b border-slate-100" style={grille(cal.jours)} data-testid={`calendrier-ligne-${idx}`}>
    <div style={{ gridRow: 1, gridColumn: 1 }} className="sticky left-0 z-[2] bg-white px-2 py-2">
      <p className="text-sm font-semibold truncate">{l.logement}</p>
      <p className="text-[11px] text-slate-500 leading-snug" data-testid={`calendrier-resume-${idx}`}>{l.nuits_occupees} occupée(s) · <b className="text-emerald-700">{l.nuits_libres} libre(s)</b><br /><span className="whitespace-nowrap">Taux {String(l.taux).replace(".", ",")} %</span></p>
    </div>
    {Array.from({ length: cal.jours }, (_, i) => i + 1).map((j) => (
      <div key={j} style={{ gridRow: 1, gridColumn: j + 1 }} className={`border-l border-slate-100 ${weekend(cal, j) ? "bg-slate-50" : "bg-white"}`} />
    ))}
    {l.sejours.map((s) => <Barre key={s.code} s={s} mois={mois} />)}
  </div>
);

const Legende = () => (
  <div className="flex flex-wrap items-center gap-4 text-xs text-slate-600" data-testid="calendrier-legende">
    <span className="flex items-center gap-1.5"><span className="h-3 w-5 rounded bg-[#FF385C]" />Airbnb</span>
    <span className="flex items-center gap-1.5"><span className="h-3 w-5 rounded bg-[#003580]" />Booking</span>
    <span className="flex items-center gap-1.5"><span className="h-3 w-5 rounded border border-dashed border-slate-500 bg-slate-300" />À venir (pas encore versé)</span>
    <span className="flex items-center gap-1.5"><span className="h-3 w-5 rounded border border-slate-200 bg-white" />Nuit libre</span>
  </div>
);

export default function Calendrier() {
  const [mois, setMois] = useState(moisCourant);
  const [cal, setCal] = useState(null);
  useEffect(() => {
    setCal(null);
    api.get("/calendrier", { params: { mois } }).then((r) => setCal(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [mois]);
  const aujourdhui = mois === moisCourant() ? new Date().getDate() : null;
  return (
    <div data-testid="calendrier-page">
      <PageHeader eyebrow="Occupation" title="Calendrier des séjours">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setMois(decaler(mois, -1))} data-testid="calendrier-precedent"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="min-w-[150px] text-center font-semibold capitalize" data-testid="calendrier-mois">{libelleMois(mois)}</span>
          <Button variant="outline" size="icon" onClick={() => setMois(decaler(mois, 1))} data-testid="calendrier-suivant"><ChevronRight className="h-4 w-4" /></Button>
          {mois !== moisCourant() && <Button variant="ghost" onClick={() => setMois(moisCourant())} data-testid="calendrier-aujourdhui">Aujourd'hui</Button>}
        </div>
      </PageHeader>
      <Panel title="Une ligne par appartement · séjours Airbnb et Booking (annulations exclues)" action={<Legende />} testId="calendrier-panel">
        {!cal ? <Loader2 className="h-6 w-6 animate-spin text-emerald-700" /> : (
          <div className="overflow-x-auto -mx-5 px-5">
            <div className="min-w-[1100px]">
              <EnTete cal={cal} aujourdhui={aujourdhui} />
              {cal.logements.map((l, i) => <Rangee key={l.logement} l={l} cal={cal} mois={mois} idx={i} />)}
              {!cal.logements.length && <p className="py-6 text-sm text-slate-500">Aucune réservation importée.</p>}
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
}
